/**
 * Debug utilities for WebCrafter
 * 
 * Enables debug logging when ?debug=true is in the URL
 * Usage: import { debugLog, isDebugMode } from '../utils/debug.js'
 */

// Check if debug mode is enabled via URL parameter
const urlParams = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null
export const isDebugMode = urlParams?.get('debug') === 'true'
export const isVerboseMode = urlParams?.get('verbose') === 'true'

// Mobile detection
export const isMobile = typeof window !== 'undefined'
    ? /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
    : false

// Override console methods if debug mode is enabled
if (isDebugMode && typeof window !== 'undefined')
{
    const originalLog = console.log;
    const originalWarn = console.warn;
    const originalError = console.error;

    // Function to send log to server
    function sendLogToServer(type, args)
    {
        const message = args.map(arg =>
        {
            try
            {
                return (typeof arg === 'object') ? JSON.stringify(arg) : String(arg);
            } catch (e)
            {
                return '[Circular/Unserializable]';
            }
        }).join(' ');

        fetch('/__log', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ type, message })
        }).catch(() => { });
    }

    console.log = function (...args)
    {
        // Send to server log file
        sendLogToServer('log', args);
        // Still print to browser console
        originalLog.apply(console, args);
    };

    console.warn = function (...args)
    {
        // Send to server log file
        sendLogToServer('warn', args);
        // Still print to browser console
        originalWarn.apply(console, args);
    };

    console.error = function (...args)
    {
        // Send to server log file
        sendLogToServer('error', args);
        // Still print to browser console
        originalError.apply(console, args);
    };

    console.log('%c[WebCrafter Debug] Console.log, console.warn, and console.error have been instrumented to log to server.', 'color: #aaa; font-style: italic;');

    // Clear log file on initial load
    fetch('/__log', {
        method: 'DELETE',
    }).catch(() => { });
}


/**
 * Log a debug message (only when debug mode is enabled)
 * @param {string} prefix - Log prefix (e.g., '[SceneManager]')
 * @param {...any} args - Arguments to log
 */
export function debugLog(prefix, ...args)
{
    if (isDebugMode)
    {
        console.log(prefix, ...args)
    }
}

/**
 * Log a debug warning (only when debug mode is enabled)
 * @param {string} prefix - Log prefix
 * @param {...any} args - Arguments to log
 */
export function debugWarn(prefix, ...args)
{
    if (isDebugMode)
    {
        console.warn(prefix, ...args)
    }
}

/**
 * Log a debug error (always shown, but with extra info in debug mode)
 * @param {string} prefix - Log prefix
 * @param {...any} args - Arguments to log
 */
export function debugError(prefix, ...args)
{
    console.error(prefix, ...args)
}

/**
 * Dump all user data properties from a scene or object tree to the console in a legible way.
 * @param {Object} scene - The Three.js scene or object to traverse
 */
export function dumpSceneUserData(scene)
{
    if (!isDebugMode || !scene) return

    console.group('%c 🔍 WebCrafter GLB User Data Import Report', 'background: #222; color: #bada55; border: 1px solid #bada55; padding: 4px; font-weight: bold; border-radius: 4px;')

    let count = 0
    let hasUserData = false

    scene.traverse((obj) =>
    {
        if (obj.userData && Object.keys(obj.userData).length > 0)
        {
            // Filter out some common internal props if necessary, but usually we show all
            if (Object.keys(obj.userData).length > 0)
            {
                if (!hasUserData) hasUserData = true
                count++

                // Fancy header for each object
                console.groupCollapsed(`%c📦 ${obj.name} %c<${obj.type}>`, 'color: #000; font-weight: bold;', 'color: #a00; font-style: italic;')

                // Use table for legibility
                console.table(obj.userData)

                // Also print raw object for inspection if needed
                // console.dir(obj.userData)

                console.groupEnd()
            }
        }
    })

    if (count === 0)
    {
        console.log('%c No custom User Data found in this GLB.', 'color: #aaa;')
    } else
    {
        console.log(`%c Found ${count} objects with User Data properties.`, 'font-weight: bold; color: #bada55;')
    }

    console.groupEnd()
}

// Log debug mode status on load
if (isDebugMode)
{
    console.log('%c[WebCrafter Debug Mode Enabled]', 'background: #222; color: #0f0; padding: 4px 8px; border-radius: 4px;')
    console.log('To disable, remove ?debug=true from the URL')
} else
{
    console.log('%c[WebCrafter] Add ?debug=true to the URL to see debug statements in the console', 'color: #888; font-style: italic;')
}
